import { CONVERSION_RATE_TYPES_VALUES, ConversionRateTypes } from 'constants/conversionTypes';

import { differenceInDays } from 'date-fns';
import { required } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';
import { DEFAULT_DATE_MASK } from 'shared/mui/DatePicker/config/defaultValues';
import { DateRangeTimezonePicker } from 'shared/mui/DatePicker/DateRangeTimezonePicker/DateRangeTimezonePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { useFilterTimezone } from 'shared/mui/Filter/hooks/useFilterTimezone';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  SelectInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';

export const useBalanceReportFilterFields = (): FilterFieldConfig[] => {
  const { resetField, setValue, watch, control } = useFormContext();
  const { timezone, onChangeTimezone } = useFilterTimezone();
  const { setAppliedFilter, appliedFilters, onReset } = useFilterContext();
  const [companies, merchants, providers] = watch(['companies', 'merchants', 'providers']);

  return [
    {
      name: 'appliedDate',
      label: 'Период отчёта',
      chipType: 'dateTimeRangeTimezone',
      chipProps: {
        mask: DEFAULT_DATE_MASK,
        onDelete: onReset,
      },
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value }, formState: { errors }, fieldState }) => (
            <DateRangeTimezonePicker
              error={!!errors.appliedDate}
              helperText={fieldState?.error?.message}
              label="Период отчёта от-до"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              required
              size="small"
              timezone={timezone}
              value={value}
            />
          )}
          rules={{
            validate: {
              required: (value) => (!!value[0] && !!value[1]) || 'Обязательно для заполнения',
              periodError: (value) => {
                const [from, to] = value;
                const intervalInDays = differenceInDays(to, from);
                return intervalInDays <= 1825;
              },
            },
          }}
        />
      ),
    },
    {
      name: 'conversionRateType',
      label: 'Курс конвертации',
      chipType: 'text',
      chipProps: {
        getName: (filterValue: string) =>
          CONVERSION_RATE_TYPES_VALUES.find((v) => filterValue === v.id)?.label || '',
        onDelete: onReset,
      },
      input: (
        <SelectInput
          choices={CONVERSION_RATE_TYPES_VALUES}
          defaultValue={ConversionRateTypes.Actual}
          fullWidth
          helperText={false}
          id="conversionRateType"
          label="Курс конвертации"
          name="conversionRateType"
          optionText="label"
          source="conversionRateType"
          validate={required()}
          variant="outlined"
        />
      ),
    },
    {
      name: 'currency',
      label: 'Валюта',
      chipType: 'text',
      chipProps: {
        onDelete: onReset,
      },
      input: (
        <ReferenceInput
          filter={{ active: true }}
          name="currency"
          perPage={1000}
          reference="currencies"
          resettable
          resource="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteInput
            clearOnBlur={false}
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            helperText={false}
            label="Валюта отчёта"
            name="currency"
            optionText={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            optionValue="alphaCode"
            validate={required()}
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'companies',
      label: 'Компании',
      chipType: 'multiselect',
      chipProps: {
        resource: 'companies',
        limitTags: 1,
        onApply: () => {
          setAppliedFilter('merchants', []);
          setAppliedFilter('providers', []);
          setAppliedFilter('merchantAccounts', []);

          resetField('merchants', { keepDirty: true });
          resetField('providers', { keepDirty: true });
          resetField('merchantAccounts', { keepDirty: true });
        },
        onDelete: () => {
          resetField('merchants', { keepDirty: true });
          resetField('providers', { keepDirty: true });
          resetField('merchantAccounts', { keepDirty: true });

          setAppliedFilter('merchants', []);
          setAppliedFilter('providers', []);
          setAppliedFilter('merchantAccounts', []);
        },
        onReset: () => {
          setValue('merchants', appliedFilters?.['merchants']);
          setValue('providers', appliedFilters?.['providers']);
          setValue('merchantAccounts', appliedFilters?.['merchantAccounts']);

          setAppliedFilter('merchants', appliedFilters?.['merchants']);
          setAppliedFilter('providers', appliedFilters?.['providers']);
          setAppliedFilter('merchantAccounts', appliedFilters?.['merchantAccounts']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          filter={{ exists: { 'merchants.merchantAccounts': true }, archive: false }}
          perPage={100}
          reference="companies"
          source="companies"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Компании"
            name="companies"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('merchants', []);
                setValue('providers', []);
                setValue('merchantAccounts', []);
              },
            })}
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'merchants',
      label: 'Мерчанты',
      chipType: 'multiselect',
      chipProps: {
        resource: 'merchants',
        limitTags: 1,
        onApply: () => {
          setAppliedFilter('providers', []);
          setAppliedFilter('merchantAccounts', []);

          resetField('providers', { keepDirty: true });
          resetField('merchantAccounts', { keepDirty: true });
        },
        onDelete: () => {
          resetField('providers', { keepDirty: true });
          resetField('merchantAccounts', { keepDirty: true });
          setAppliedFilter('providers', []);
          setAppliedFilter('merchantAccounts', []);
        },
        onReset: () => {
          setValue('providers', appliedFilters?.['providers']);
          setValue('merchantAccounts', appliedFilters?.['merchantAccounts']);

          setAppliedFilter('providers', appliedFilters?.['providers']);
          setAppliedFilter('merchantAccounts', appliedFilters?.['merchantAccounts']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!companies?.length}
          filter={{ companies, exists: { merchantAccounts: true }, archive: false }}
          perPage={100}
          reference="merchants"
          source="merchants"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !companies?.length,
              disabledHint: 'Сначала выберите компанию',
              onChange: () => {
                setValue('providers', []);
                setValue('merchantAccounts', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Мерчанты"
            name="merchants"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'providers',
      label: 'Провайдеры',
      chipType: 'multiselect',
      chipProps: {
        resource: 'providers',
        limitTags: 1,
        onApply: () => {
          setAppliedFilter('merchantAccounts', []);
          resetField('merchantAccounts', { keepDirty: true });
        },
        onDelete: () => {
          resetField('merchantAccounts', { keepDirty: true });
          setAppliedFilter('merchantAccounts', []);
        },
        onReset: () => {
          setValue('merchantAccounts', appliedFilters?.['merchantAccounts']);
          setAppliedFilter('merchantAccounts', appliedFilters?.['merchantAccounts']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!merchants?.length}
          filter={{ merchantAccounts: { merchants } }}
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="providers"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !merchants?.length,
              disabledHint: 'Сначала выберите мерчанта',
              onChange: () => {
                setValue('merchantAccounts', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Провайдеры"
            name="providers"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'merchantAccounts',
      label: 'Счета мерчантов',
      chipType: 'multiselect',
      chipProps: {
        resource: 'merchant_accounts',
        limitTags: 1,
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!providers?.length}
          filter={{ merchants, provider: providers }}
          perPage={100}
          reference="merchant_accounts"
          source="merchantAccounts"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !providers?.length,
              disabledHint: 'Сначала выберите провайдера',
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Счета мерчантов"
            name="merchantAccounts"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
  ];
};
